import React, { useMemo } from "react";
import { Line } from "react-chartjs-2";

import "../../chart/registerChartJs";
import {
  formatPortfolioChartAxisLabels,
  formatPortfolioTooltipTitle,
  formatPortfolioUsdAmount,
} from "../../pages/Portfolio/chartDataUtils";

function PortfolioBalanceLineChart({ labels, values, loading, range }) {
  const hasSeries =
    Array.isArray(labels) &&
    Array.isArray(values) &&
    labels.length > 0 &&
    labels.length === values.length;

  const axisLabels = useMemo(
    () => (hasSeries ? formatPortfolioChartAxisLabels(labels, range ?? "6m") : []),
    [labels, range, hasSeries],
  );

  const { data, options } = useMemo(() => {
    const safeLabels = hasSeries ? axisLabels : ["—"];
    const safeValues = hasSeries ? values : [0];

    return {
      data: {
        labels: safeLabels,
        datasets: [
          {
            label: "Balance",
            data: safeValues,
            borderColor: "#329BFF",
            backgroundColor: "rgba(50, 155, 255, 0.14)",
            fill: true,
            tension: 0.35,
            pointRadius: 0,
            pointHoverRadius: 4,
            borderWidth: 2,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        resizeDelay: 120,
        interaction: { mode: "index", intersect: false },
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              title: (tooltipItems) => {
                if (!tooltipItems.length || !hasSeries) return "";
                const idx = tooltipItems[0].dataIndex;
                const raw = labels[idx];
                return formatPortfolioTooltipTitle(raw);
              },
              label: (ctx) => {
                const v = ctx.parsed.y;
                return `${ctx.dataset.label}: ${formatPortfolioUsdAmount(v)}`;
              },
            },
          },
        },
        scales: {
          x: {
            grid: { color: "rgba(255, 255, 255, 0.06)" },
            ticks: {
              color: "#989898",
              maxRotation: 0,
              autoSkip: true,
              maxTicksLimit: 16,
            },
            border: { display: false },
          },
          y: {
            grid: { color: "rgba(255, 255, 255, 0.06)" },
            ticks: {
              color: "#989898",
              callback: (value) => formatPortfolioUsdAmount(value),
            },
            border: { display: false },
          },
        },
      },
    };
  }, [labels, values, hasSeries, axisLabels]);

  if (loading) {
    return (
      <div className="portfolioLineChart portfolioLineChart--empty d-flex align-items-center justify-content-center">
        Loading chart…
      </div>
    );
  }

  if (!hasSeries) {
    return (
      <div className="portfolioLineChart portfolioLineChart--empty d-flex align-items-center justify-content-center text-center px-3">
        No balance history for this range.
      </div>
    );
  }

  return (
    <div className="portfolioLineChart">
      <Line data={data} options={options} />
    </div>
  );
}

export default PortfolioBalanceLineChart;
